import Pusher from 'pusher-js'
import { useGameStore } from '@/stores/gameStore'
import type PublicRouterUpdate from '@/types/PublicRouterUpdate'
import Backend from '@/models/backend'
import { logger } from '@/models/logger.ts'
import { t } from '@/locales'
import { getWebSocketUpdateQueue } from '@/models/WebSocketUpdateQueue'


export default class WebSocketHandler {
    private pusher: Pusher;
    private gameStore = useGameStore();
    private backend = new Backend();
    private lastTimestamp = Date.now();
    private sleepThreshold = 5 * 60 * 1000; // 5 minute
    private lastPingTime = 0;
    private latencyInterval: ReturnType<typeof setInterval> | null = null;

    constructor() {
        // Connect out-of-sync callback to the WebSocket queue
        getWebSocketUpdateQueue().setOutOfSyncCallback(() => this.outOfSync())
        logger.info('connect to ' + import.meta.env.VITE_WS_HOST);
        this.gameStore.loadingText = t('common.status.connecting')

        this.pusher = new Pusher('2ca8002757656b9f485e6fec23d5b722', {
            wsPort: import.meta.env.VITE_WS_PORT,
            wsHost: import.meta.env.VITE_WS_HOST,
            wssPort: import.meta.env.VITE_WSS_PORT,
            forceTLS: import.meta.env.VITE_WS_FORCE_TLS === 'true',
            disableStats: true,
            enabledTransports: ['ws','wss'],
            authEndpoint: import.meta.env.VITE_API_URL + 'auth/broadcast?token=' + this.gameStore.playerToken,
            cluster: 'mt1',
        });

        // Websocket connection established
        this.pusher.connection.bind('connected', () => {
            logger.info('Websocket connected');
            if (!this.gameStore.websocketInitialSyncCompleted) {
                this.subscribeToChannels();
            }
            this.gameStore.loadingText = t('common.status.syncingAssets')
            this.startLatencyMeasurement();
        });

        // Listen for pong responses to measure latency
        this.pusher.connection.bind('message', (message: { event: string }) => {
            if (message.event === 'pusher:pong' && this.lastPingTime > 0) {
                this.gameStore.websocketLatency = Math.round(performance.now() - this.lastPingTime);
                this.lastPingTime = 0;
            }
        });

        // Websocket connection lost
        this.pusher.connection.bind('disconnected', () => {
            logger.info('Websocket disconnected');
            this.stopLatencyMeasurement();
            this.outOfSync();
        });

        // Websocket error
        this.pusher.connection.bind('error', (err: string) => {
            logger.info('Websocket error', err);
            // Attempt to reconnect by resubscribing to channels
            if (this.pusher.connection.state === 'connected') {
                logger.info('Reconnecting to channels');
                this.subscribeToChannels();
            }
        });

        // unavailable
        this.pusher.connection.bind('unavailable', () => {
            logger.info('Websocket unavailable');
            this.outOfSync();
        });

        // Check for wake-up from sleep state
        document.addEventListener('visibilitychange', this.checkForWakeUp.bind(this));
        setInterval(this.checkForWakeUp.bind(this), 1000);
    }

    private checkForWakeUp() {
        const now = Date.now();
        if (document.hidden) {
            // Page is not visible, update the timestamp
            this.lastTimestamp = now;
        } else {
            // Page is visible, check if the time difference exceeds the threshold
            if (now - this.lastTimestamp > this.sleepThreshold) {
                // The page was likely in a sleep state
                logger.info('Page came back from a sleep state');
                //this.outOfSync();
                // Reload page
                window.location.reload()
            }
            // Update the timestamp
            this.lastTimestamp = now;
        }
    }

    /**
     * Wait for a websocket connection to be established
     *
     * @param callback
     * @param interval
     */
    public waitForConnection(callback: () => void, interval: number) {
        if (this.checkIfPublicConnected() && this.checkIfPrivateConnected()) {
            callback();
        } else {
            setTimeout(() => {
                this.waitForConnection(callback, interval);
            }, interval);
        }
    }

    /**
     * Check if the channels are connected
     */
    public checkIfPublicConnected(): boolean {
        // check if channel exists
        if (this.pusher.channel('public') === undefined) {
            return false;
        }

        return this.pusher.channel('public').subscribed;
    }

    public checkIfPrivateConnected(): boolean {
        // check if channel exists
        if (this.pusher.channel('private-player-' + this.gameStore.authPlayerId) === undefined) {
            return false;
        }

        return this.pusher.channel('private-player-' + this.gameStore.authPlayerId).subscribed;
    }

    public subscribeToChannels() {
        // Subscribe to public channel
        const publicChannel = this.pusher.subscribe('public');
        publicChannel.bind('pusher:subscription_succeeded', () => {
            logger.info('Subscribed to public channel');
        });
        publicChannel.bind('update', (data: PublicRouterUpdate) => {
            getWebSocketUpdateQueue().enqueueRaw(data.data, 'public', data.message_count)
        });

        // Subscribe to private channel
        const privateChannel = this.pusher.subscribe('private-player-' + this.gameStore.authPlayerId);
        privateChannel.bind('pusher:subscription_succeeded', () => {
            logger.info('Subscribed to private channel : ' + 'private-player-' + this.gameStore.authPlayerId);
        });
        privateChannel.bind('update', (data: { data: string; message_count: number }) => {
            getWebSocketUpdateQueue().enqueueRaw(data.data, 'private', data.message_count)
        });
    }

    private startLatencyMeasurement() {
        this.stopLatencyMeasurement();
        // Send initial ping
        this.sendPing();
        // Send ping every 10 seconds
        this.latencyInterval = setInterval(() => this.sendPing(), 10000);
    }

    private stopLatencyMeasurement() {
        if (this.latencyInterval) {
            clearInterval(this.latencyInterval);
            this.latencyInterval = null;
        }
    }

    private sendPing() {
        if (this.pusher.connection.state === 'connected') {
            this.lastPingTime = performance.now();
            this.pusher.connection.send_event('pusher:ping', {});
        }
    }

    private outOfSync() {
        if (!this.gameStore.gameOutOfSync) {
            this.gameStore.gameOutOfSync = true;
        }
    }

}
